import type { IChartApiBase, ISeriesApi, Logical } from "lightweight-charts";

type AxisKey = number | string;
type Range = { from: number; to: number };
type Anchor = { key: AxisKey; offset: number };
type MoveHandler = (param: any) => void;

function anchorAt(keys: AxisKey[], logical: number): Anchor {
  const index = Math.max(0, Math.min(keys.length - 1, Math.floor(logical)));
  const key = keys[index];
  const adjacent = index === keys.length - 1 ? index - 1 : index + 1;
  const other = keys[adjacent];
  if (typeof key === "number" && typeof other === "number") {
    const step = (other - key) / (adjacent - index);
    return { key: key + (logical - index) * step, offset: 0 };
  }
  return { key, offset: logical - index };
}

function logicalAt(keys: AxisKey[], anchor: Anchor): number {
  let lo = 0;
  let hi = keys.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (keys[mid] < anchor.key) lo = mid + 1;
    else hi = mid;
  }
  if (keys[lo] === anchor.key) return lo + anchor.offset;
  const left = Math.max(0, Math.min(keys.length - 2, lo - 1));
  const a = keys[left];
  const b = keys[left + 1];
  if (typeof anchor.key === "number" && typeof a === "number" && typeof b === "number" && b !== a) {
    return left + (anchor.key - a) / (b - a) + anchor.offset;
  }
  return Math.min(lo, keys.length - 1) + anchor.offset;
}

/** Float noise from a rebuilt time grid must still count as the same point. */
function findKeyIndex(list: AxisKey[], key: AxisKey): number {
  const exact = list.indexOf(key);
  if (exact >= 0) return exact;
  if (typeof key !== "number") return -1;
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < list.length; i++) {
    const candidate = list[i];
    if (typeof candidate !== "number") continue;
    const dist = Math.abs(candidate - key);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  // 0.1ms: tighter than real samples, wider than float error on the timestamp.
  return best >= 0 && bestDist <= 1e-4 ? best : -1;
}

function keysOverlap(prev: AxisKey[], next: AxisKey[]): boolean {
  if (!prev.length || !next.length) return false;
  const nextSet = new Set(next);
  if (prev.some((key) => nextSet.has(key))) return true;
  if (prev.some((key) => typeof key !== "number") || next.some((key) => typeof key !== "number")) {
    return false;
  }
  const prevNums = prev as number[];
  const nextNums = next as number[];
  const prevMin = Math.min(...prevNums);
  const prevMax = Math.max(...prevNums);
  const nextMin = Math.min(...nextNums);
  const nextMax = Math.max(...nextNums);
  return prevMin <= nextMax && nextMin <= prevMax;
}

function pointTime(param: any): AxisKey | null {
  let time: AxisKey | null = null;
  param?.seriesData?.forEach?.((row: { time?: AxisKey; value?: number }) => {
    if (time == null && row?.time != null && row.value != null) time = row.time;
  });
  return time;
}

/** Keep refreshes anchored to data coordinates, even when new points shift indices. */
export function createChartRefreshState() {
  let keys: AxisKey[] = [];
  let fittedRange: Range | null = null;
  let hover: { key: AxisKey; y: number } | null = null;
  let updating = false;
  let frameId = 0;
  let savedRange: { from: Anchor; to: Anchor } | null = null;
  let priceRanges: { id: string; range: Range }[] = [];

  return {
    onCrosshairMove(param: any, onMove: MoveHandler) {
      if (updating) return;
      const onPlot = Boolean(
        param?.point &&
          param.point.x >= 0 &&
          param.point.y >= 0 &&
          param.seriesData?.size &&
          keys.length,
      );
      let key: AxisKey | null = null;
      if (onPlot) {
        const time = pointTime(param);
        // Line/scatter series store the axis key as `time`. Timeseries stores
        // a slot index, which is not in the key list — fall back to the slot.
        if (time != null && keys.indexOf(time) >= 0) key = time;
        else if (typeof param.logical === "number" && Number.isFinite(param.logical)) {
          const index = Math.max(0, Math.min(keys.length - 1, Math.round(param.logical)));
          key = keys[index] ?? null;
        }
      }
      hover = key != null ? { key, y: param.point.y } : null;
      onMove(param);
    },

    /** True after beginUpdate when this refresh should keep the current viewport. */
    keepingRange() {
      return savedRange != null;
    },

    beginUpdate(chart: IChartApiBase<number>) {
      cancelAnimationFrame(frameId);
      const range = chart.timeScale().getVisibleLogicalRange();
      const changed = range && fittedRange && (
        Math.abs(range.from - fittedRange.from) > 0.01 ||
        Math.abs(range.to - fittedRange.to) > 0.01
      );
      savedRange = keys.length && range && (changed || hover)
        ? { from: anchorAt(keys, range.from), to: anchorAt(keys, range.to) }
        : null;
      priceRanges = ["left", "right"].flatMap((id) => {
        const scale = chart.priceScale(id);
        const range = !scale.options().autoScale ? scale.getVisibleRange() : null;
        return range ? [{ id, range }] : [];
      });
      updating = true;
    },

    finishUpdate(
      chart: IChartApiBase<number>,
      nextKeys: AxisKey[],
      series: ISeriesApi<"Line", number>[],
      onMove: MoveHandler,
    ): boolean {
      const overlaps = keysOverlap(keys, nextKeys);
      keys = nextKeys;
      const from = savedRange && overlaps ? logicalAt(keys, savedRange.from) : NaN;
      const to = savedRange && overlaps ? logicalAt(keys, savedRange.to) : NaN;
      const range =
        Number.isFinite(from) && Number.isFinite(to) && to > from ? { from, to } : null;
      const preserved = range != null;
      const applyRange = () => {
        if (range) chart.timeScale().setVisibleLogicalRange(range);
        else chart.timeScale().fitContent();
        priceRanges.forEach(({ id, range: priceRange }) => {
          if (overlaps) chart.priceScale(id).setVisibleRange(priceRange);
          else chart.priceScale(id).applyOptions({ autoScale: true });
        });
      };
      applyRange();

      // LWC applies time-scale changes in its render frame, and a crosshair
      // update can fit the chart again. Put the viewport back after that frame,
      // then place the tooltip on the same data point.
      frameId = requestAnimationFrame(() => {
        applyRange();
        if (!preserved) fittedRange = chart.timeScale().getVisibleLogicalRange();
        // Programmatic crosshair movement doesn't emit an event: refresh the HTML
        // tooltip explicitly using the new series and values at the same data point.
        const index = hover ? findKeyIndex(keys, hover.key) : -1;
        const x = index >= 0
          ? chart.timeScale().logicalToCoordinate(index as Logical)
          : null;
        const seriesData = new Map();
        let target: { series: ISeriesApi<"Line", number>; time: number; value: number } | null = null;
        let distance = Infinity;
        if (hover && x != null) {
          for (const line of series) {
            if (!line.options().visible) continue;
            const point = line.dataByIndex(index);
            if (!point || !("value" in point) || point.value == null) continue;
            seriesData.set(line, point);
            const y = line.priceToCoordinate(point.value);
            if (y != null && Math.abs(y - hover.y) < distance) {
              distance = Math.abs(y - hover.y);
              target = { series: line, time: point.time, value: point.value };
            }
          }
        }
        if (hover && x != null && target) {
          chart.setCrosshairPosition(target.value, target.time, target.series);
          onMove({ logical: index, point: { x, y: hover.y }, seriesData });
        } else {
          hover = null;
          chart.clearCrosshairPosition();
          onMove({});
        }
        // Crosshair placement can schedule another fit. Pin the viewport last.
        if (preserved && range) chart.timeScale().setVisibleLogicalRange(range);
        updating = false;
      });
      return preserved;
    },

    markFitted(chart: IChartApiBase<number>) {
      fittedRange = chart.timeScale().getVisibleLogicalRange();
    },

    reset() {
      cancelAnimationFrame(frameId);
      keys = [];
      fittedRange = null;
      hover = null;
      updating = false;
      savedRange = null;
      priceRanges = [];
    },
  };
}
